import Papa from "papaparse";

export interface SatelliteRow {
  name: string;
  norad: string;
  cospar: string;
  mass: number;
  span: number;
  latCoef: number;
  radCoef: number;
  dragArea: number;
  dragCoef: number;
  liftArea: number;
  liftCoef: number;
  lateralArea: number;
  radiationArea: number;
}

const DEFAULT_ROW: Omit<
  SatelliteRow,
  "name" | "norad" | "cospar" | "id"
> = {
  mass: 1000,
  span: 20,
  latCoef: 0,
  radCoef: 0,
  dragArea: 0,
  dragCoef: 0,
  liftArea: 0,
  liftCoef: 0,
  lateralArea: 0,
  radiationArea: 400,
};

const REQUIRED_COLUMNS = ["name", "norad", "cospar"];

const NUMERIC_COLUMNS = [
  "mass",
  "span",
  "latCoef",
  "radCoef",
  "dragArea",
  "dragCoef",
  "liftArea",
  "liftCoef",
  "lateralArea",
  "radiationArea",
] as const;

export const processCsv = (
  file: File
): Promise<SatelliteRow[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,

      complete: (results) => {
        try {
          const headers = results.meta.fields ?? [];

          // 1. Validar cabeceras obligatorias
          const missingColumns = REQUIRED_COLUMNS.filter(
            (column) => !headers.includes(column)
          );

          if (missingColumns.length > 0) {
            throw new Error(
              `Invalid CSV format. Missing required columns: ${missingColumns.join(
                ", "
              )}`
            );
          }

          const rows: SatelliteRow[] = results.data.map(
            (rawRow: any, index: number) => {
              const rowNumber = index + 2; // +1 por índice, +1 por cabecera

              // 2. Validar campos obligatorios
              for (const column of REQUIRED_COLUMNS) {
                const value = rawRow[column];

                if (
                  value === undefined ||
                  value === null ||
                  String(value).trim() === ""
                ) {
                  throw new Error(
                    `Row ${rowNumber}: '${column}' is required`
                  );
                }
              }

              // 3. Validar campos numéricos si existen
              for (const column of NUMERIC_COLUMNS) {
                const value = rawRow[column];

                if (
                  value !== undefined &&
                  value !== null &&
                  value !== ""
                ) {
                  const parsed = Number(value);

                  if (Number.isNaN(parsed)) {
                    throw new Error(
                      `Row ${rowNumber}: '${column}' must be a number`
                    );
                  }
                }
              }

              return {
                ...DEFAULT_ROW,

                name: rawRow.name.trim(),
                norad: rawRow.norad.trim(),
                cospar: rawRow.cospar.trim(),

                mass:
                  rawRow.mass !== undefined &&
                  rawRow.mass !== ""
                    ? Number(rawRow.mass)
                    : DEFAULT_ROW.mass,

                span:
                  rawRow.span !== undefined &&
                  rawRow.span !== ""
                    ? Number(rawRow.span)
                    : DEFAULT_ROW.span,

                latCoef:
                  rawRow.latCoef !== undefined &&
                  rawRow.latCoef !== ""
                    ? Number(rawRow.latCoef)
                    : DEFAULT_ROW.latCoef,

                radCoef:
                  rawRow.radCoef !== undefined &&
                  rawRow.radCoef !== ""
                    ? Number(rawRow.radCoef)
                    : DEFAULT_ROW.radCoef,

                dragArea:
                  rawRow.dragArea !== undefined &&
                  rawRow.dragArea !== ""
                    ? Number(rawRow.dragArea)
                    : DEFAULT_ROW.dragArea,

                dragCoef:
                  rawRow.dragCoef !== undefined &&
                  rawRow.dragCoef !== ""
                    ? Number(rawRow.dragCoef)
                    : DEFAULT_ROW.dragCoef,

                liftArea:
                  rawRow.liftArea !== undefined &&
                  rawRow.liftArea !== ""
                    ? Number(rawRow.liftArea)
                    : DEFAULT_ROW.liftArea,

                liftCoef:
                  rawRow.liftCoef !== undefined &&
                  rawRow.liftCoef !== ""
                    ? Number(rawRow.liftCoef)
                    : DEFAULT_ROW.liftCoef,

                lateralArea:
                  rawRow.lateralArea !== undefined &&
                  rawRow.lateralArea !== ""
                    ? Number(rawRow.lateralArea)
                    : DEFAULT_ROW.lateralArea,

                radiationArea:
                  rawRow.radiationArea !== undefined &&
                  rawRow.radiationArea !== ""
                    ? Number(rawRow.radiationArea)
                    : DEFAULT_ROW.radiationArea,

                id: String(index),
              };
            }
          );

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      },

      error: reject,
    });
  });
};